<template>
  <el-drawer
    :visible.sync="dialogVisible"
    :with-header="false"
    size="1100px"
    append-to-body
    :modal-append-to-body="true"
    custom-class="vtm-shelf-drawer"
    :before-close="beforeClose"
    @open="open"
    @close="close"
  >
    <div class="shelfd" v-loading="loading" element-loading-text="Loading...">
      <div class="shelfd__head">
        <div class="shelfd__head-main">
          <div class="shelfd__title-row">
            <span class="shelfd__title">Shelf Products</span>
            <span class="shelfd__machine">{{ userEquipment.equipmentName }}</span>
            <span class="shelfd__no vtm-mono">{{ userEquipment.equipmentNo }}</span>
          </div>
          <div class="shelfd__subtitle">Assign products to shelves, then set price, stock, and low-stock alerts for each item.</div>
        </div>
        <button type="button" class="shelfd__close" @click="handlerCancel"><i class="el-icon-close"></i></button>
      </div>

      <div class="shelfd__body">
        <!-- Left Panel: Shelves -->
        <div class="shelfd__shelves">
          <div class="shelfd__panel-head">
            <span class="shelfd__panel-title">Shelves</span>
            <button type="button" class="shelfd__addshelf" @click="addRow"><i class="el-icon-plus"></i> Add Shelf</button>
          </div>

          <div class="shelfd__shelves-scroll">
            <div v-if="!hasRows" class="shelfd__empty">
              <i class="el-icon-files"></i>
              <p>No shelves yet</p>
              <span>Click "Add Shelf" to start</span>
            </div>
            <div v-else class="shelfd__shelflist">
              <div
                v-for="(products, rowIndex) in sortedRows"
                :key="rowIndex"
                :class="['shelf-card', { 'shelf-card--active': selectedRowIndex == rowIndex, 'shelf-card--dragover': dragOverShelf == rowIndex }]"
                @click="selectRow(rowIndex)"
                @dragover.prevent="onDragOverShelf(rowIndex)"
                @dragleave="onDragLeaveShelf(rowIndex)"
                @drop.prevent="onDropShelf($event, rowIndex)"
              >
                <div class="shelf-card__head">
                  <span class="shelf-card__badge">Shelf {{ rowIndex }}</span>
                  <span class="shelf-card__count vtm-num">{{ products.length }}</span>
                  <button type="button" class="shelf-card__del" @click.stop="handlerCloseRow(rowIndex)">
                    <i class="el-icon-delete"></i>
                  </button>
                </div>

                <div class="shelf-card__products" v-if="products.length > 0">
                  <div
                    :class="['shelf-product', { 'shelf-product--dragover': dragOverProduct === rowIndex + ':' + productIndex }]"
                    v-for="(product, productIndex) in products"
                    :key="productIndex"
                    draggable="true"
                    @dragstart="onDragStartShelfProduct($event, rowIndex, productIndex)"
                    @dragend="onDragEnd"
                    @dragover.prevent.stop="onDragOverProduct(rowIndex, productIndex)"
                    @dragleave.stop="onDragLeaveProduct(rowIndex, productIndex)"
                    @drop.prevent.stop="onDropOnProduct($event, rowIndex, productIndex)"
                  >
                    <div class="shelf-product__img">
                      <el-image :src="product.productImage" fit="contain">
                        <div slot="error" class="shelf-product__img-fallback"><i class="el-icon-picture-outline"></i></div>
                      </el-image>
                    </div>
                    <div class="shelf-product__body">
                      <div class="shelf-product__head">
                        <span class="shelf-product__name" :title="product.productName">{{ product.productName }}</span>
                        <span v-if="isCanonicalInstance(product) && hasMultipleInstances(product)" class="shelf-product__master">
                          <i class="el-icon-star-on"></i> Editable
                        </span>
                        <span v-if="isSimilarProductInRows(product)" class="vtm-pill vtm-pill--warn">{{ $t("productSimilar.hasSimilarGroup") }}</span>
                        <button type="button" class="shelf-product__remove" @click.stop="removeProduct(rowIndex, productIndex)">
                          <i class="el-icon-close"></i>
                        </button>
                      </div>
                      <div class="shelf-product__fields">
                        <label class="shelf-product__field">
                          <span class="shelf-product__field-label">Price</span>
                          <el-input
                            v-model="product.retailPrice"
                            size="mini"
                            :disabled="!isCanonicalInstance(product)"
                            @input="syncProductSpec(product)"
                          ></el-input>
                        </label>
                        <label class="shelf-product__field">
                          <span class="shelf-product__field-label">Stock</span>
                          <el-input
                            v-model="product.nowStock"
                            size="mini"
                            :disabled="!isCanonicalInstance(product)"
                            @input="syncProductSpec(product)"
                          ></el-input>
                        </label>
                        <label class="shelf-product__field">
                          <span class="shelf-product__field-label">Low Stock Alert</span>
                          <el-input
                            v-model="product.stockAlarm"
                            size="mini"
                            :disabled="!isCanonicalInstance(product)"
                            @input="syncProductSpec(product)"
                          ></el-input>
                        </label>
                      </div>
                      <div v-if="!isCanonicalInstance(product)" class="shelf-product__locked-hint">
                        <i class="el-icon-lock"></i> The product price, stock, and alerts are editable on its first instance — Shelf {{ canonicalShelfOf(product) }}
                      </div>
                      <div class="shelf-product__bar">
                        <div
                          class="shelf-product__bar-fill"
                          :class="'shelf-product__bar-fill--' + stockStatus(product)"
                          :style="{ width: stockPct(product) + '%' }"
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="shelf-card__empty" v-else>
                  <i class="el-icon-goods"></i>
                  <span>Click a product to add</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Panel: Product Library -->
        <div class="shelfd__library">
          <div class="shelfd__panel-head">
            <span class="shelfd__panel-title">Product Library</span>
            <span class="shelfd__panel-tip">{{
              hasRows ? `${rowCount} shelves, ${selectedProductCount} products in current` : "Add shelf first"
            }}</span>
          </div>
          <el-input
            v-model="search.productName"
            placeholder="Search products"
            size="small"
            clearable
            prefix-icon="el-icon-search"
            class="shelfd__search"
            @keyup.enter.native="currentChange(1)"
            @clear="currentChange(1)"
          ></el-input>

          <div class="shelfd__library-scroll">
            <div v-if="productList.length === 0" class="shelfd__empty">
              <i class="el-icon-search"></i>
              <p>No products found</p>
              <span>Try different keywords</span>
            </div>
            <div v-else class="shelfd__libgrid">
              <div
                v-for="product in productList"
                :key="product.productId"
                class="lib-card"
                draggable="true"
                @click="addProductToRow($event, product)"
                @dragstart="onDragStartLibrary($event, product)"
                @dragend="onDragEnd"
              >
                <div class="lib-card__img">
                  <el-image :src="product.productImage" fit="contain">
                    <div slot="error" class="lib-card__img-fallback"><i class="el-icon-picture-outline"></i></div>
                  </el-image>
                </div>
                <div class="lib-card__name" :title="product.productName">{{ product.productName }}</div>
                <span v-if="isCandidateSimilarProduct(product)" class="vtm-pill vtm-pill--warn lib-card__tag">{{
                  $t("productSimilar.hasSimilarGroup")
                }}</span>
              </div>
            </div>
          </div>
          <el-pagination
            v-if="productList.length > 0"
            layout="prev, pager, next"
            :current-page="page.pageIndex"
            :total="page.totalSize"
            :page-size="page.pageSize"
            :pager-count="5"
            small
            class="shelfd__pagination"
            @current-change="currentChange"
          ></el-pagination>
        </div>
      </div>

      <div class="shelfd__foot">
        <el-button size="small" @click="handlerCancel">Cancel</el-button>
        <el-button :loading="submitLoading" size="small" type="primary" @click="handlerSubmit">Confirm</el-button>
      </div>
    </div>
  </el-drawer>
</template>

<script>
  export default {
    name: "OpenDoorLine",
    props: {
      show: {
        type: Boolean,
        default: false
      },
      userEquipment: {
        type: Object,
        default: () => ({})
      }
    },
    data() {
      return {
        rows: {},
        selectedRowIndex: null,
        seqCounter: 0, // 同一商品跨货架放多份时，靠这个序号判断谁是"第一份"（可编辑的那份）
        productList: [],
        search: {
          productName: ""
        },
        page: {
          pageSize: 12,
          pageIndex: 1,
          totalSize: 0
        },
        loading: false,
        submitLoading: false,
        dragPayload: null, // { type: 'library', product } | { type: 'shelf', rowKey, index }
        dragOverShelf: null,
        dragOverProduct: null, // "rowKey:productIndex" — 精确到某张商品卡片的拖拽悬停高亮
        startData: ""
      };
    },
    computed: {
      dialogVisible: {
        get() {
          return this.show;
        },
        set(value) {
          if (!value) {
            this.close();
          }
        }
      },
      sortedRows() {
        return Object.keys(this.rows)
          .sort((a, b) => a - b)
          .reduce((acc, key) => {
            acc[key] = this.rows[key];
            return acc;
          }, {});
      },
      hasRows() {
        return Object.keys(this.rows).length > 0;
      },
      rowCount() {
        return Object.keys(this.rows).length;
      },
      selectedProductCount() {
        if (this.selectedRowIndex === null || !this.rows[this.selectedRowIndex]) {
          return 0;
        }
        return this.rows[this.selectedRowIndex].length;
      }
    },
    methods: {
      normalizeProduct(raw) {
        const productId = raw.goodsId || raw.productId || "";
        return {
          ...raw,
          id: raw.id || null,
          productId,
          goodsId: productId,
          productName: raw.goodsName || raw.productName || raw.name || "",
          productImage: raw.url || raw.goodsUrl || raw.image || "",
          retailPrice: raw.sellingPrice || raw.retailPrice || "",
          nowStock: raw.currentInventoryLevel || raw.nowStock || "",
          stockAlarm: raw.inventoryAlertCount || raw.stockAlarm || "",
          netWeight: raw.netWeight || raw.weight || ""
        };
      },
      // AI 视觉摄像头识别不区分货架，机器只认商品本身——所以"低库存"看的是这个商品
      // 在整台机器里的库存。Stock 现在只有"第一份"能编辑，其它份数都是它的镜像，
      // 所以这里直接取"第一份"的值即可，不能把每一份都加起来算总数（那样会重复计数）
      productTotalStock(product) {
        const canonical = this.findCanonicalProduct(product.productId);
        return Number((canonical || product).nowStock) || 0;
      },
      // 库存条：总库存≤0 为 crit，≤警戒线为 warn，否则 ok（配比条宽度用于可视化，不是精确百分比）
      stockStatus(product) {
        const stock = this.productTotalStock(product);
        const alarm = Number(product.stockAlarm) || 0;
        if (stock <= 0) {
          return "crit";
        }
        if (alarm > 0 && stock <= alarm) {
          return "warn";
        }
        return "ok";
      },
      // 没有真实的"最大容量"可用（同一商品到底能放多少台完全看进货情况，货架也不代表容量上限），
      // 条形宽度只是按警戒线的倍数做一个粗略的可视化参考，不代表精确占比
      stockPct(product) {
        const stock = this.productTotalStock(product);
        const alarm = Number(product.stockAlarm) || 0;
        const denom = Math.max(alarm * 3, 10);
        return Math.max(0, Math.min(100, Math.round((stock / denom) * 100)));
      },
      // 同一商品跨货架放多份时，只有"第一份"（_seq 最小的那份）能编辑 Price/Alert，
      // 其余份数只能改 Stock（那是真实的物理数量，本来就该各货架各算各的）
      findCanonicalProduct(productId) {
        let canonical = null;
        Object.keys(this.rows).forEach(key => {
          (this.rows[key] || []).forEach(item => {
            if (item.productId === productId) {
              if (!canonical || (item._seq ?? Infinity) < (canonical._seq ?? Infinity)) {
                canonical = item;
              }
            }
          });
        });
        return canonical;
      },
      isCanonicalInstance(product) {
        const canonical = this.findCanonicalProduct(product.productId);
        return !canonical || canonical === product;
      },
      // 这个商品是不是放了不止一份（用来决定要不要显示"Master"标记——只有一份的时候没必要标）
      hasMultipleInstances(product) {
        let count = 0;
        Object.keys(this.rows).some(key => {
          (this.rows[key] || []).forEach(item => {
            if (item.productId === product.productId) {
              count++;
            }
          });
          return count > 1;
        });
        return count > 1;
      },
      // 找到"第一份"（master）所在的货架号，用于在锁定的字段旁边提示"去哪一层改"
      canonicalShelfOf(product) {
        const canonical = this.findCanonicalProduct(product.productId);
        if (!canonical) {
          return null;
        }
        const key = Object.keys(this.rows).find(rowKey => (this.rows[rowKey] || []).includes(canonical));
        return key || null;
      },
      // Price / Stock / Alert 都是商品本身的属性，不是货架的属性：只有 master（第一份）能编辑，
      // 编辑时同步覆盖这个商品在其它货架上的所有份数，避免同一商品在机器里数据不一致
      syncProductSpec(product) {
        Object.keys(this.rows).forEach(key => {
          (this.rows[key] || []).forEach(item => {
            if (item.productId === product.productId && item !== product) {
              item.retailPrice = product.retailPrice;
              item.nowStock = product.nowStock;
              item.stockAlarm = product.stockAlarm;
            }
          });
        });
      },
      handlerCancel() {
        if (this.rows && JSON.stringify(this.rows) !== this.startData) {
          this.$confirm("You have unsaved changes. Close this dialog anyway?", "Notice", {
            confirmButtonText: "Confirm",
            cancelButtonText: "Cancel"
          })
            .then(() => {
              this.close();
            })
            .catch(() => {});
          return;
        }
        this.close();
      },
      beforeClose(done) {
        if (this.rows && JSON.stringify(this.rows) !== this.startData) {
          this.$confirm("You have unsaved changes. Close this dialog anyway?", "Notice", {
            confirmButtonText: "Confirm",
            cancelButtonText: "Cancel"
          })
            .then(() => done())
            .catch(() => {});
          return;
        }
        done();
      },
      async open() {
        this.rows = {};
        this.selectedRowIndex = null;
        this.seqCounter = 0;
        await this.currentChange(1);
        await this.fetchRowConfig();
        // 这款机型固定 5 层货架：不管后端已配置几层，都把 1-5 补齐，
        // 方便操作员一进来就能看到全部实体货架，而不是只看到已经配过产品的那几层。
        // 补齐后重新记录 startData，避免刚打开、什么都没改就关闭时被误判为"有未保存的更改"
        this.ensureMinShelves(5);
        this.startData = JSON.stringify(this.rows);
        if (Object.keys(this.rows).length > 0) {
          this.selectedRowIndex = Object.keys(this.rows)[0];
        }
      },
      ensureMinShelves(count) {
        for (let i = 1; i <= count; i++) {
          if (!this.rows[i]) {
            this.$set(this.rows, i, []);
          }
        }
      },
      fetchRowConfig() {
        return this.$api
          .request(this.$url.OpenDoor.getAisleGoodsConfig, this.$method.get, {
            userEquipmentId: this.userEquipment.id
          })
          .then(res => {
            if (res.code !== 0) {
              return;
            }
            const data = res.data || {};
            const mapRows = {};
            if (Array.isArray(data)) {
              data.forEach(item => {
                const rowKey = item.floorNo || item.aisleNo || 1;
                if (!mapRows[rowKey]) {
                  mapRows[rowKey] = [];
                }
                mapRows[rowKey].push(this.normalizeProduct(item));
              });
            } else {
              Object.keys(data).forEach(rowKey => {
                const rowItems = Array.isArray(data[rowKey]) ? data[rowKey] : [];
                mapRows[rowKey] = rowItems.map(this.normalizeProduct);
              });
            }
            // 按货架号从小到大给已有数据编号，作为"谁是第一份、可编辑"的基准
            // （后端不记录配置的先后顺序，货架号从小到大是最合理的默认）
            Object.keys(mapRows)
              .map(Number)
              .sort((a, b) => a - b)
              .forEach(rowKey => {
                mapRows[rowKey].forEach(item => {
                  item._seq = this.seqCounter++;
                });
              });
            this.rows = mapRows;
            this.startData = JSON.stringify(mapRows);
          });
      },
      handlerSubmit() {
        const openDoorAisleGoodsDTOS = {};
        Object.keys(this.rows).forEach(aisleNo => {
          const products = this.rows[aisleNo].filter(p => p.goodsId || p.productId);
          if (products.length > 0) {
            // Map key 必须是 Integer 类型（后端定义为 Map<Integer, List<...>>）
            const floorNoKey = parseInt(aisleNo, 10);
            openDoorAisleGoodsDTOS[floorNoKey] = products.map((product, index) =>
              this.buildSubmitProduct(product, floorNoKey, index)
            );
          }
        });

        if (Object.keys(openDoorAisleGoodsDTOS).length === 0) {
          this.$message.warning("Please configure at least one product.");
          return;
        }

        const payload = {
          userEquipmentId: String(this.userEquipment.id),
          openDoorAisleGoodsDTOS: openDoorAisleGoodsDTOS
        };

        this.submitLoading = true;
        this.$api
          .request(this.$url.OpenDoor.saveOrEditAisleGoods, this.$method.post, payload)
          .then(res => {
            if (res.code !== 0) {
              this.$message.warning(res.msg || "Save failed.");
              return;
            }
            this.$message.success("Saved successfully.");
            this.$emit("refresh");
            this.startData = JSON.stringify(this.rows);
            this.close();
          })
          .catch(() => {
            this.$message.error("Save failed.");
          })
          .finally(() => {
            this.submitLoading = false;
          });
      },
      buildSubmitProduct(product, floorNo, sort) {
        const item = {
          id: product.id || null,
          goodsId: product.goodsId || product.productId,
          goodsName: product.productName || product.goodsName || product.name || "",
          url: product.productImage || product.goodsUrl || product.url || "",
          sellingPrice: product.retailPrice || product.sellingPrice || 0,
          currentInventoryLevel: product.nowStock || product.currentInventoryLevel || 0,
          inventoryAlertCount: product.stockAlarm || product.inventoryAlertCount || 0,
          floorNo,
          sort
        };
        this.getReadonlySimilarFields().forEach(field => {
          delete item[field];
        });
        return item;
      },
      getReadonlySimilarFields() {
        return [
          "similarGroupId",
          "similarGroupName",
          "similarGroupMemberCount",
          "hasSimilarGroup",
          "hasSimilarInCurrentMachine",
          "similarGroupConfiguredCount",
          "similarToConfiguredProduct"
        ];
      },
      isValidSimilarGroupId(similarGroupId) {
        return similarGroupId !== undefined && similarGroupId !== null && similarGroupId !== "" && similarGroupId !== 0 && similarGroupId !== "0";
      },
      getSimilarGroupKey(similarGroupId) {
        return this.isValidSimilarGroupId(similarGroupId) ? String(similarGroupId) : "";
      },
      isSimilarProductInRows(product) {
        if (product.hasSimilarInCurrentMachine === true) return true;
        return this.hasSameSimilarGroupInRows(product);
      },
      hasSameSimilarGroupInRow(products, product) {
        if (product.hasSimilarGroup !== true) return false;
        const similarGroupKey = this.getSimilarGroupKey(product.similarGroupId);
        if (!similarGroupKey) return false;
        return (products || []).some(
          item =>
            String(item.goodsId || item.productId || "") !== String(product.goodsId || product.productId || "") &&
            item.hasSimilarGroup === true &&
            this.getSimilarGroupKey(item.similarGroupId) === similarGroupKey
        );
      },
      hasSameSimilarGroupInRows(product) {
        return Object.keys(this.rows).some(key => this.hasSameSimilarGroupInRow(this.rows[key], product));
      },
      hasSimilarGroupInRows(product) {
        const similarGroupKey = this.getSimilarGroupKey(product.similarGroupId);
        if (!similarGroupKey) return false;
        return Object.keys(this.rows).some(key =>
          (this.rows[key] || []).some(item => this.getSimilarGroupKey(item.similarGroupId) === similarGroupKey)
        );
      },
      isCandidateSimilarProduct(product) {
        return product.similarToConfiguredProduct || this.hasSimilarGroupInRows(product);
      },
      currentChange(pageIndex) {
        this.page.pageIndex = pageIndex || 1;
        return this.fetchProducts();
      },
      fetchProducts() {
        this.loading = true;
        const params = {
          pageIndex: this.page.pageIndex,
          pageSize: this.page.pageSize,
          goodsName: this.search.productName,
          skuResult: 2
        };
        return this.$api
          .request(this.$url.OpenDoor.getSkuList, this.$method.get, params)
          .then(res => {
            if (res.code === 0 && res.data) {
              const records = res.data.records || [];
              this.productList = records.map(this.normalizeProduct);
              this.page.totalSize = Number(res.data.total || 0);
            }
          })
          .finally(() => {
            this.loading = false;
          });
      },
      close() {
        this.search.productName = "";
        this.$emit("close");
      },
      addRow() {
        const sorted = Object.keys(this.rows)
          .map(Number)
          .sort((a, b) => a - b);
        const newRowKey = sorted.length ? sorted[sorted.length - 1] + 1 : 1;
        this.$set(this.rows, newRowKey, []);
        this.selectedRowIndex = newRowKey;
      },
      handlerCloseRow(index) {
        this.$delete(this.rows, index);
        const adjustedRows = {};
        Object.keys(this.rows).forEach((key, idx) => {
          adjustedRows[idx + 1] = this.rows[key];
        });
        this.rows = adjustedRows;
        if (this.selectedRowIndex === index) {
          this.selectedRowIndex = null;
        }
      },
      selectRow(index) {
        this.selectedRowIndex = index;
      },
      addProductToRow(event, product) {
        if (this.selectedRowIndex === null) {
          this.$message.warning("Please select a shelf first.");
          return;
        }
        const added = this.insertProductIntoShelf(this.selectedRowIndex, product);
        this.animateItem(event, added);
      },
      // 把商品放进某个货架：新增自商品库时 id 置空（新建行），跨货架拖动时保留原 id（原地更新 floorNo）。
      // atIndex 不传就加到货架末尾；传了就插到那个位置（拖到某张卡片上、想插在它前面时用）
      insertProductIntoShelf(rowKey, product, { preserveId = false, atIndex = null } = {}) {
        const shelfProducts = this.rows[rowKey];
        if (!shelfProducts) {
          return false;
        }
        if (shelfProducts.some(item => item.productId === product.productId)) {
          this.$message.warning("This product is already on that shelf.");
          return false;
        }
        if (this.hasSameSimilarGroupInRows(product)) {
          this.$notify({
            title: this.$t("components.OpenDoorLine.574153-0"),
            message: this.$t("components.OpenDoorLine.855420-1")
          });
        }
        const cloned = JSON.parse(JSON.stringify(product));
        if (preserveId) {
          // 跨货架"移动"：还是同一份数据，_seq 也带过去，别让它因为换了个壳丢掉"第一份"身份
          cloned._seq = product._seq;
        } else {
          cloned.id = null;
          cloned._seq = this.seqCounter++;
          // 这个商品已经在机器别的货架上了：价格/库存/警戒线以 master 为准，
          // 新加的这份直接对齐，不用等下一次编辑才同步
          const canonical = this.findCanonicalProduct(product.productId);
          if (canonical) {
            cloned.retailPrice = canonical.retailPrice;
            cloned.nowStock = canonical.nowStock;
            cloned.stockAlarm = canonical.stockAlarm;
          }
        }
        if (atIndex === null || atIndex >= shelfProducts.length) {
          shelfProducts.push(cloned);
        } else {
          shelfProducts.splice(atIndex, 0, cloned);
        }
        return true;
      },
      removeProduct(rowIndex, productIndex) {
        this.rows[rowIndex].splice(productIndex, 1);
      },
      // 同一货架内纯排序：不影响机器怎么识别商品，纯粹方便操作员让屏幕顺序对上实际货架摆放
      reorderWithinShelf(rowKey, fromIndex, toIndex) {
        const list = this.rows[rowKey];
        if (!list || fromIndex === toIndex) {
          return;
        }
        const [item] = list.splice(fromIndex, 1);
        // 移除后，原本 toIndex 之后的项都往前挪了一位
        const insertAt = fromIndex < toIndex ? toIndex - 1 : toIndex;
        list.splice(insertAt, 0, item);
      },
      // ================= 拖拽：货架间移动商品 / 从商品库拖入货架 =================
      onDragStartLibrary(event, product) {
        this.dragPayload = { type: "library", product };
        event.dataTransfer.effectAllowed = "copy";
        event.dataTransfer.setData("text/plain", product.productId || "");
      },
      onDragStartShelfProduct(event, rowKey, index) {
        this.dragPayload = { type: "shelf", rowKey, index };
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("text/plain", String(index));
      },
      onDragOverShelf(rowKey) {
        this.dragOverShelf = rowKey;
      },
      onDragLeaveShelf(rowKey) {
        if (this.dragOverShelf == rowKey) {
          this.dragOverShelf = null;
        }
      },
      onDragEnd() {
        this.dragPayload = null;
        this.dragOverShelf = null;
        this.dragOverProduct = null;
      },
      onDropShelf(event, rowKey) {
        const payload = this.dragPayload;
        this.dragOverShelf = null;
        this.dragPayload = null;
        if (!payload) {
          return;
        }
        if (payload.type === "library") {
          this.insertProductIntoShelf(rowKey, payload.product);
          return;
        }
        if (payload.type === "shelf") {
          if (String(payload.rowKey) === String(rowKey)) {
            return;
          }
          const sourceList = this.rows[payload.rowKey];
          const product = sourceList && sourceList[payload.index];
          if (!product) {
            return;
          }
          const added = this.insertProductIntoShelf(rowKey, product, { preserveId: true });
          if (added) {
            sourceList.splice(payload.index, 1);
          }
        }
      },
      // 拖到某张具体商品卡片上：同货架内 = 排序，跨货架 = 移动并插到这张卡片前面。
      // 用 .stop 让这个事件不再往上冒泡到货架级别的 onDropShelf（那个是"拖到空白处=加到末尾"）
      onDragOverProduct(rowKey, productIndex) {
        this.dragOverShelf = rowKey;
        this.dragOverProduct = rowKey + ":" + productIndex;
      },
      onDragLeaveProduct(rowKey, productIndex) {
        if (this.dragOverProduct === rowKey + ":" + productIndex) {
          this.dragOverProduct = null;
        }
      },
      onDropOnProduct(event, rowKey, targetIndex) {
        const payload = this.dragPayload;
        this.dragOverShelf = null;
        this.dragOverProduct = null;
        this.dragPayload = null;
        if (!payload) {
          return;
        }
        if (payload.type === "library") {
          this.insertProductIntoShelf(rowKey, payload.product, { atIndex: targetIndex });
          return;
        }
        if (payload.type === "shelf") {
          if (String(payload.rowKey) === String(rowKey)) {
            this.reorderWithinShelf(rowKey, payload.index, targetIndex);
            return;
          }
          const sourceList = this.rows[payload.rowKey];
          const product = sourceList && sourceList[payload.index];
          if (!product) {
            return;
          }
          const added = this.insertProductIntoShelf(rowKey, product, { preserveId: true, atIndex: targetIndex });
          if (added) {
            sourceList.splice(payload.index, 1);
          }
        }
      },
      animateItem(event, isSuccess) {
        let element = event.target;
        if (element.tagName !== "IMG") {
          const closestProductItem = element.closest(".lib-card");
          if (closestProductItem) {
            element = closestProductItem.querySelector("img");
          }
        }
        if (!element) {
          return;
        }
        const animationClass = isSuccess ? "animate__fadeOutLeft" : "animate__shakeX";
        element.classList.add("animate__animated", animationClass);
        element.addEventListener(
          "animationend",
          () => {
            element.classList.remove("animate__animated", animationClass);
          },
          { once: true }
        );
      }
    }
  };
</script>

<style lang="scss" scoped>
  .shelfd {
    display: flex;
    flex-direction: column;
    height: 100%;
    font-family: var(--vtm-body);
  }

  // ---- 头部 ----
  .shelfd__head {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 20px 24px 16px;
    background: linear-gradient(180deg, var(--vtm-surface) 0%, var(--vtm-surface-2) 100%);
    border-bottom: 1px solid var(--vtm-line);
  }
  .shelfd__head-main {
    flex: 1;
    min-width: 0;
  }
  .shelfd__title-row {
    display: flex;
    align-items: baseline;
    gap: 10px;
    flex-wrap: wrap;
  }
  .shelfd__title {
    font-family: var(--vtm-display);
    font-size: 19px;
    font-weight: 650;
    color: var(--vtm-heading);
  }
  .shelfd__machine {
    font-size: 13px;
    color: var(--vtm-ink-2);
  }
  .shelfd__no {
    font-size: 12px;
    color: var(--vtm-ink-3);
  }
  .shelfd__subtitle {
    margin-top: 4px;
    font-size: 12.5px;
    color: var(--vtm-ink-2);
  }
  .shelfd__close {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: none;
    border-radius: var(--vtm-r-sm);
    background: transparent;
    color: var(--vtm-ink-2);
    font-size: 16px;
    cursor: pointer;
    transition: background-color var(--vtm-speed) ease, color var(--vtm-speed) ease;

    &:hover {
      background: var(--vtm-well);
      color: var(--vtm-crit);
    }
  }

  // ---- 主体：左（货架）右（商品库）----
  .shelfd__body {
    flex: 1;
    min-height: 0;
    display: flex;
    gap: 0;
  }

  .shelfd__panel-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 16px 20px 10px;
  }
  .shelfd__panel-title {
    font-family: var(--vtm-display);
    font-size: 14px;
    font-weight: 650;
    color: var(--vtm-heading);
  }
  .shelfd__panel-tip {
    font-size: 11.5px;
    color: var(--vtm-ink-3);
  }
  .shelfd__addshelf {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 5px 12px;
    border: 1px solid var(--vtm-line);
    border-radius: 999px;
    background: var(--vtm-well);
    color: var(--vtm-mint-ink);
    font-size: 12.5px;
    font-weight: 600;
    cursor: pointer;
    transition: border-color var(--vtm-speed), background-color var(--vtm-speed);

    &:hover {
      border-color: var(--vtm-mint);
      background: var(--vtm-mint-soft);
    }
  }

  .shelfd__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: 200px;
    color: var(--vtm-ink-3);
    text-align: center;

    i {
      font-size: 36px;
      margin-bottom: 4px;
    }
    p {
      margin: 0;
      font-size: 13.5px;
      color: var(--vtm-ink-2);
    }
    span {
      font-size: 12px;
    }
  }

  // ---- 左：货架 ----
  .shelfd__shelves {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    border-right: 1px solid var(--vtm-line);
  }
  .shelfd__shelves-scroll {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 0 20px 20px;
  }
  .shelfd__shelflist {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .shelf-card {
    padding: 12px;
    background: var(--vtm-surface);
    border: 1px solid var(--vtm-line);
    border-left: 3px solid transparent;
    border-radius: var(--vtm-r-md);
    cursor: pointer;
    transition: border-color var(--vtm-speed), box-shadow var(--vtm-speed), background-color var(--vtm-speed);

    &:hover {
      border-color: var(--vtm-line-2);
      box-shadow: var(--vtm-e1);
    }
  }
  .shelf-card--active {
    border-left-color: var(--vtm-mint);
    background: var(--vtm-mint-soft);
  }
  .shelf-card--dragover {
    border-color: var(--vtm-mint);
    border-style: dashed;
    background: var(--vtm-mint-soft);
  }
  .shelf-card__head {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 10px;
  }
  .shelf-card__badge {
    font-family: var(--vtm-display);
    font-size: 13px;
    font-weight: 650;
    color: var(--vtm-heading);
  }
  .shelf-card__count {
    font-size: 11.5px;
    font-weight: 700;
    color: var(--vtm-ink-2);
    background: var(--vtm-well);
    border-radius: 999px;
    padding: 1px 8px;
  }
  .shelf-card__del {
    margin-left: auto;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border: none;
    border-radius: var(--vtm-r-sm);
    background: transparent;
    color: var(--vtm-ink-3);
    cursor: pointer;
    transition: background-color var(--vtm-speed), color var(--vtm-speed);

    &:hover {
      background: var(--vtm-crit-soft);
      color: var(--vtm-crit);
    }
  }
  .shelf-card__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    height: 76px;
    color: var(--vtm-ink-3);
    background: var(--vtm-well);
    border-radius: var(--vtm-r-sm);
    font-size: 12px;

    i {
      font-size: 20px;
    }
  }

  .shelf-card__products {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 10px;
  }

  .shelf-product {
    display: flex;
    gap: 10px;
    padding: 10px;
    background: var(--vtm-well);
    border: 1px solid var(--vtm-line);
    border-radius: var(--vtm-r-sm);
    cursor: grab;
    transition: border-color var(--vtm-speed), box-shadow var(--vtm-speed);

    &:active {
      cursor: grabbing;
    }
  }
  .shelf-product--dragover {
    border-color: var(--vtm-mint);
    box-shadow: -3px 0 0 0 var(--vtm-mint);
  }
  .shelf-product__img {
    width: 52px;
    height: 52px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;

    ::v-deep .el-image {
      width: 52px;
      height: 52px;
    }
  }
  .shelf-product__img-fallback {
    width: 52px;
    height: 52px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--vtm-surface);
    border-radius: var(--vtm-r-sm);
    color: var(--vtm-ink-3);
    font-size: 18px;
  }
  .shelf-product__body {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .shelf-product__head {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .shelf-product__name {
    flex: 1;
    min-width: 0;
    font-size: 12.5px;
    font-weight: 600;
    color: var(--vtm-ink);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .shelf-product__remove {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border: none;
    border-radius: 50%;
    background: transparent;
    color: var(--vtm-ink-3);
    cursor: pointer;

    &:hover {
      background: var(--vtm-crit-soft);
      color: var(--vtm-crit);
    }
  }
  .shelf-product__fields {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
  .shelf-product__field {
    flex: 1 1 64px;
    min-width: 64px;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .shelf-product__field-label {
    font-size: 10px;
    font-weight: 600;
    color: var(--vtm-ink-3);
  }
  .shelf-product__master {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    font-size: 10.5px;
    font-weight: 600;
    color: var(--vtm-ok);
    white-space: nowrap;

    i {
      font-size: 11px;
    }
  }
  .shelf-product__locked-hint {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 10px;
    line-height: 1.35;
    color: var(--vtm-ink-3);

    i {
      font-size: 11px;
      flex-shrink: 0;
    }
  }
  .shelf-product__bar {
    height: 4px;
    border-radius: 4px;
    background: var(--vtm-surface);
    overflow: hidden;
  }
  .shelf-product__bar-fill {
    height: 100%;
    border-radius: 4px;

    &--ok {
      background: linear-gradient(90deg, var(--vtm-mint-dim), var(--vtm-mint));
    }
    &--warn {
      background: var(--vtm-warn);
    }
    &--crit {
      background: var(--vtm-crit);
    }
  }

  // ---- 右：商品库 ----
  .shelfd__library {
    width: 340px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
  }
  .shelfd__search {
    margin: 0 20px 12px;
    width: auto;
  }
  .shelfd__library-scroll {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 0 20px;
  }
  .shelfd__libgrid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
    padding-bottom: 12px;
  }
  .lib-card {
    position: relative;
    padding: 10px;
    background: var(--vtm-surface);
    border: 1px solid var(--vtm-line);
    border-radius: var(--vtm-r-sm);
    cursor: pointer;
    text-align: center;
    min-width: 0;
    transition: border-color var(--vtm-speed), box-shadow var(--vtm-speed);

    &:hover {
      border-color: var(--vtm-mint);
      box-shadow: var(--vtm-e1);
    }
  }
  .lib-card__img {
    width: 56px;
    height: 56px;
    margin: 0 auto 6px;

    ::v-deep .el-image {
      width: 56px;
      height: 56px;
    }
  }
  .lib-card__img-fallback {
    width: 56px;
    height: 56px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--vtm-well);
    border-radius: var(--vtm-r-sm);
    color: var(--vtm-ink-3);
    font-size: 18px;
  }
  .lib-card__name {
    font-size: 12px;
    color: var(--vtm-ink-2);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .lib-card__tag {
    display: block;
    width: fit-content;
    margin: 6px auto 0;
  }
  .shelfd__pagination {
    text-align: center;
    padding: 4px 20px 16px;
  }

  // ---- 底部 ----
  .shelfd__foot {
    flex-shrink: 0;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding: 14px 24px;
    border-top: 1px solid var(--vtm-line);
  }
</style>
